import moment from 'moment';
import type { Options, Types, Warning } from '../types';
import { resolveId } from './util';
import type { OutTypes } from './schedules';

export function parsePeriod (
  period:        Types.period,
  scheduleStart: moment.Moment,
  options:       Options,
  warnings:      Warning[]
): OutTypes.period {
  // should warn only once
  let warned = false;

  const resolveGuid = (guid: Parameters<typeof resolveId>[0]) => resolveId(guid, { generateIfMissing: !!options.allowGeneratedGUIDs });

  // parse range
  const ranges = (period.Calendar ?? '')
    .split(',')
    // parse each range
    .map(x => {
      // date ranges on the form "20/8-23/8"
      const [_start, _end] = x.split('-');
      if (!_start || !_end) return;

      const start = moment.utc(`${_start}/${scheduleStart.year()}`, 'DD/MM/YYYY');
      const end   = moment.utc(`${_end}/${scheduleStart.year()}`,   'DD/MM/YYYY');
      if (!start.isValid() || !end.isValid()) {
        if (warned) return;
        warned = true;
        warnings.push({
          code:    'period.problem_parsing_range',
          context: period
        });
        return;
      }

      // add one year to the range if it is before the schedule start
      if (start.isBefore(scheduleStart)) {
        start.add(1, 'year');
        end  .add(1, 'year');
      }

      return { start, end };
    })
    .filter((x): x is NonNullable<typeof x> => !!x)
    // make the ranges ordered by incrementing by a year if needed
    .reduce<{ start: moment.Moment, end: moment.Moment }[]>((acc, x) => {
      const prev = acc.at(-1);
      if (!prev) {
        acc.push(x);
      } else {
        while (prev.end.isAfter(x.start)) {
          x.start.add(1, 'year');
          x.end  .add(1, 'year');
        }
        acc.push(x);
      }
      return acc;
    }, [])
    // convert to whole week
    .map(x => {
      const start = x.start.startOf('isoWeek');
      const end   = x.end  .endOf  ('isoWeek');
      return { start, end };
    })
    // remove duplicates (if a week was split into two ranges)
    .filter((x, i, arr) => {
      for (let j = i + 1; j < arr.length; j++) {
        if (x.start.isSame(arr[j].start) && x.end.isSame(arr[j].end)) return false;
      }
      return true;
    });

  return {
    ids:         resolveGuid(period.GUID),
    displayName: period.TextBelowSchedule,
    ranges
  };
}


export function fromWeekNumberRange (
  ids:             string,
  weekNumberRange: string,
  scheduleStart:   moment.Moment
): OutTypes.period {
  const startYear         = scheduleStart.year();
  const scheduleStartWeek = scheduleStart.startOf('isoWeek');

  const ranges = weekNumberRange
    // parse the range to positive intervals that lie after the schedule start
    .split(',')
    .map(x => {
      const interval = x.split('-').map(x => parseInt(x.trim()));
      const startWeekNumber = interval[0];
      const endWeekNumber   = interval[1] ?? startWeekNumber;

      const start = moment.utc()
        .isoWeekYear(startYear)
        .isoWeek(startWeekNumber)
        .startOf('isoWeek');
      if (start.isBefore(scheduleStartWeek)) {
        start.add(1, 'year')
          .isoWeek(startWeekNumber)
          .startOf('isoWeek');
      }

      const end = moment.utc()
        .isoWeekYear(start.year())
        .isoWeek(endWeekNumber)
        .endOf('isoWeek');
      if (end.isBefore(start)) {
        end.add(1, 'year')
          .isoWeek(endWeekNumber)
          .endOf('isoWeek');
      }

      return [ start, end ];
    })
    // make the ranges ordered by incrementing by a year if needed
    .reduce<{ start: moment.Moment, end: moment.Moment }[]>((acc, x) => {
      const prev = acc.at(-1);
      if (!prev) {
        acc.push({ start: x[0], end: x[1] });
      } else {
        while (prev.end.isAfter(x[0])) {
          const startWeekNumber = x[0].isoWeek();
          const endWeekNumber   = x[1].isoWeek();
          // preserve the week number
          x[0].add(1, 'year').isoWeek(startWeekNumber).startOf('isoWeek');
          x[1].add(1, 'year').isoWeek(endWeekNumber  ).endOf  ('isoWeek');
        }
        acc.push({ start: x[0], end: x[1] });
      }
      return acc;
    }, []);

  return {
    ids,
    displayName: weekNumberRange,
    ranges:      ranges
  };
}